import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/UserAvatar';
import { toast } from 'sonner';
import { Save, Phone, Mail, Building2, User as UserIcon, ShieldCheck, MessageSquare, KeyRound, Trash2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

// Phone validation matches backend util
const PHONE_RE = /^\+?[1-9]\d{6,14}$/;
const normalizePhone = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (s.startsWith('+')) return '+' + s.slice(1).replace(/\D/g, '');
  return s.replace(/\D/g, '');
};

export const ProfilePage = () => {
  const { user, reloadUser } = useAuth();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [delPw, setDelPw] = useState('');
  const [delConfirm, setDelConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || '', phone: user.phone || '' });
  }, [user]);

  const dirty = user && (form.name !== (user.name || '') || form.phone !== (user.phone || ''));
  const normalized = normalizePhone(form.phone);
  const phoneValid = form.phone === '' || PHONE_RE.test(normalized);

  const save = async () => {
    if (!phoneValid) { toast.error('Invalid phone number — use international format like +31612345678'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: form.name.trim(), phone: normalized }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      toast.success('Profile updated');
      if (reloadUser) await reloadUser();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const isGoogleOnly = user.is_guest;
  const pwValid = pw.next.length >= 8 && pw.next === pw.confirm && pw.current.length > 0 && pw.next !== pw.current;

  const changePassword = async () => {
    if (!pwValid) return;
    setPwSaving(true);
    try {
      const r = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Password change failed');
      toast.success('Password updated');
      setPw({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPwSaving(false);
    }
  };

  const roleLabel = user.is_master_admin ? 'Master Admin' : user.role === 'admin' ? 'Administrator' : 'User';

  const canSelfDelete = !user.is_master_admin && !user.is_billing_owner && !user.is_guest;
  const deleteAccount = async () => {
    if (!window.confirm('Delete your account permanently? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(isGoogleOnly ? { confirm: true } : { password: delPw }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Deletion failed');
      localStorage.removeItem('auth_token'); sessionStorage.removeItem('auth_token');
      toast.success('Account deleted');
      window.location.href = '/';
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        <div className="flex items-center gap-4">
          <UserAvatar user={user} size="xl" />
          <div>
            <h1 className="text-2xl font-semibold">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary"><ShieldCheck className="h-3 w-3 mr-1" />{roleLabel}</Badge>
              {user.company && <Badge variant="outline">{user.company}</Badge>}
            </div>
          </div>
        </div>

        <Card className="p-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><UserIcon className="h-4 w-4" />Full name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="profile-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Mail className="h-4 w-4" />Email</Label>
            <Input value={user.email} disabled data-testid="profile-email-input" />
            <p className="text-xs text-muted-foreground">Contact your master admin to change this.</p>
          </div>

          {user.company && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2"><Building2 className="h-4 w-4" />Company</Label>
              <Input value={user.company} disabled />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />Phone number
              <Badge variant="outline" className="ml-1 text-[10px] gap-1">
                <MessageSquare className="h-2.5 w-2.5" /> Required for messaging
              </Badge>
            </Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+31612345678"
              className={!phoneValid ? 'border-rose-500 focus-visible:ring-rose-400' : ''}
              data-testid="profile-phone-input"
            />
            <p className="text-xs text-muted-foreground">
              {phoneValid
                ? 'International format (E.164). Dashes, spaces and parentheses are stripped automatically.'
                : 'Invalid format. Use e.g. +31612345678.'}
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-muted/40">
            <Button
              variant="outline"
              onClick={() => setForm({ name: user.name || '', phone: user.phone || '' })}
              disabled={!dirty || saving}
            >
              Discard
            </Button>
            <Button
              onClick={save}
              disabled={!dirty || !phoneValid || saving}
              data-testid="profile-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </Card>

        {!isGoogleOnly && (
          <Card className="p-6 space-y-5" data-testid="change-password-card">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-semibold">Change password</h2>
            </div>
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <PasswordInput
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
                placeholder="Your current password"
                testId="pw-current"
              />
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <PasswordInput
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                placeholder="At least 8 characters"
                testId="pw-new"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <PasswordInput
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                placeholder="Re-type the new password"
                className={pw.confirm && pw.confirm !== pw.next ? 'border-rose-500 focus-visible:ring-rose-400' : ''}
                testId="pw-confirm"
              />
              {pw.confirm && pw.confirm !== pw.next && (
                <p className="text-xs text-rose-600">Passwords don't match.</p>
              )}
            </div>
            <div className="flex items-center justify-end pt-2 border-t border-muted/40">
              <Button onClick={changePassword} disabled={!pwValid || pwSaving} data-testid="pw-save-btn">
                <KeyRound className="h-4 w-4 mr-2" />
                {pwSaving ? 'Updating...' : 'Update password'}
              </Button>
            </div>
          </Card>
        )}

        {!user.phone && (
          <Card className="p-4 border-amber-200 bg-amber-50" data-testid="profile-phone-warning">
            <p className="text-sm text-amber-900">
              <strong>Heads up:</strong> the Messages feature is locked until you add a phone number above.
            </p>
          </Card>
        )}

        <Card className="p-6 space-y-4 border-rose-200 bg-rose-50/50" data-testid="delete-account-card">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <h2 className="text-lg font-semibold text-rose-800">Delete account</h2>
          </div>
          <p className="text-sm text-rose-800/80">
            Permanently deletes your login and sign-in sessions. Your company's operational
            planning data stays with the company. This cannot be undone.
          </p>
          {!canSelfDelete ? (
            <p className="text-sm text-rose-900 bg-rose-100 rounded-md px-3 py-2" data-testid="delete-account-blocked">
              {user.is_master_admin
                ? 'The platform owner account cannot be self-deleted.'
                : user.is_billing_owner
                  ? "You're the billing owner for your company. Transfer billing before deleting your account."
                  : 'Guest accounts have nothing to delete.'}
            </p>
          ) : (
            <>
              {!isGoogleOnly ? (
                <div className="space-y-1.5 max-w-sm">
                  <Label>Confirm your password</Label>
                  <PasswordInput
                    value={delPw}
                    onChange={(e) => setDelPw(e.target.value)}
                    placeholder="Your password"
                    testId="delete-account-password"
                  />
                </div>
              ) : (
                <label className="flex items-center gap-2 text-sm text-rose-900">
                  <input type="checkbox" checked={delConfirm} onChange={(e) => setDelConfirm(e.target.checked)} data-testid="delete-account-confirm" />
                  I understand this permanently deletes my account.
                </label>
              )}
              <div className="flex justify-end pt-1">
                <Button
                  variant="destructive"
                  onClick={deleteAccount}
                  disabled={deleting || (isGoogleOnly ? !delConfirm : delPw.length === 0)}
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting ? 'Deleting…' : 'Delete my account'}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
